import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trash2, Plus, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/games")({
  component: GamesAdmin,
});

type Game = {
  id?: string; name: string; color: string;
  open_time: string; close_time: string; result_time: string;
  min_bet: number; max_bet: number; active: boolean; position: number;
  rule_a_start?: string | null; rule_a_end?: string | null; rule_a_min?: number | null; rule_a_max?: number | null;
  rule_b_start?: string | null; rule_b_end?: string | null; rule_b_min?: number | null; rule_b_max?: number | null;
  max_unique_numbers?: number | null;
  max_same_amount_numbers?: number | null;
};
const empty: Game = { name: "", color: "#7c3aed", open_time: "09:00", close_time: "11:00", result_time: "11:30", min_bet: 10, max_bet: 10000, active: true, position: 0,
  rule_a_start: "", rule_a_end: "", rule_a_min: null, rule_a_max: null,
  rule_b_start: "", rule_b_end: "", rule_b_min: null, rule_b_max: null,
  max_unique_numbers: null, max_same_amount_numbers: null };

function GamesAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin_games"],
    queryFn: async () => (await supabase.from("games").select("*").order("position")).data ?? [],
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Game>(empty);

  async function save() {
    if (!form.name) return toast.error("Name required");
    const norm = (t?: string | null) => (t && t.length > 0 ? (t.length === 5 ? `${t}:00` : t) : null);
    const num = (n: number | null | undefined) => (n === null || n === undefined || Number.isNaN(n) ? null : Number(n));
    const payload: any = {
      ...form,
      rule_a_start: norm(form.rule_a_start), rule_a_end: norm(form.rule_a_end),
      rule_a_min: num(form.rule_a_min), rule_a_max: num(form.rule_a_max),
      rule_b_start: norm(form.rule_b_start), rule_b_end: norm(form.rule_b_end),
      rule_b_min: num(form.rule_b_min), rule_b_max: num(form.rule_b_max),
      max_unique_numbers: num(form.max_unique_numbers),
      max_same_amount_numbers: num(form.max_same_amount_numbers),
    };
    // sanity: if any rule field set, require both start and end
    for (const r of ["a", "b"] as const) {
      const s = payload[`rule_${r}_start`], e = payload[`rule_${r}_end`];
      const mn = payload[`rule_${r}_min`], mx = payload[`rule_${r}_max`];
      if ((s || e || mn != null || mx != null) && (!s || !e || mn == null || mx == null)) {
        return toast.error(`Rule ${r.toUpperCase()} needs start, end, min and max`);
      }
      if (mn != null && mx != null && mn > mx) return toast.error(`Rule ${r.toUpperCase()} min cannot exceed max`);
    }
    const { error } = form.id
      ? await supabase.from("games").update(payload).eq("id", form.id)
      : await supabase.from("games").insert(payload);
    if (error) return toast.error(error.message);
    toast.success("Saved");
    setOpen(false);
    setForm(empty);
    qc.invalidateQueries();
  }
  async function toggle(id: string, active: boolean) {
    await supabase.from("games").update({ active }).eq("id", id);
    qc.invalidateQueries();
  }
  async function del(id: string) {
    if (!confirm("Delete game?")) return;
    await supabase.from("games").delete().eq("id", id);
    qc.invalidateQueries();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black">Games</h1>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(empty); }}>
          <DialogTrigger asChild>
            <Button onClick={() => setForm(empty)} className="gradient-primary shadow-purple"><Plus className="mr-2 h-4 w-4" /> Add Game</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{form.id ? "Edit" : "New"} Game</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Color</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
              <div><Label>Position</Label><Input type="number" value={form.position} onChange={(e) => setForm({ ...form, position: Number(e.target.value) })} /></div>
              <div><Label>Open Time</Label><Input type="time" value={form.open_time} onChange={(e) => setForm({ ...form, open_time: e.target.value })} /></div>
              <div><Label>Close Time</Label><Input type="time" value={form.close_time} onChange={(e) => setForm({ ...form, close_time: e.target.value })} /></div>
              <div><Label>Result Time</Label><Input type="time" value={form.result_time} onChange={(e) => setForm({ ...form, result_time: e.target.value })} /></div>

              {(["a", "b"] as const).map((r) => (
                <div key={r} className="col-span-2 space-y-2 rounded-xl border p-3">
                  <p className="text-sm font-bold">Number Bet Limit — Rule {r.toUpperCase()} <span className="text-xs font-normal text-muted-foreground">(per number 1–100)</span></p>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Start Time</Label><Input type="time" value={(form as any)[`rule_${r}_start`] ?? ""} onChange={(e) => setForm({ ...form, [`rule_${r}_start`]: e.target.value } as any)} /></div>
                    <div><Label>End Time</Label><Input type="time" value={(form as any)[`rule_${r}_end`] ?? ""} onChange={(e) => setForm({ ...form, [`rule_${r}_end`]: e.target.value } as any)} /></div>
                    <div><Label>Min / number ₹</Label><Input type="number" value={(form as any)[`rule_${r}_min`] ?? ""} onChange={(e) => setForm({ ...form, [`rule_${r}_min`]: e.target.value === "" ? null : Number(e.target.value) } as any)} /></div>
                    <div><Label>Max / number ₹</Label><Input type="number" value={(form as any)[`rule_${r}_max`] ?? ""} onChange={(e) => setForm({ ...form, [`rule_${r}_max`]: e.target.value === "" ? null : Number(e.target.value) } as any)} /></div>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Leave all four blank to disable Rule {r.toUpperCase()}.</p>
                </div>
              ))}

              <div className="col-span-2 space-y-2 rounded-xl border p-3">
                <p className="text-sm font-bold">Per-User Selection Limits <span className="text-xs font-normal text-muted-foreground">(this game cycle)</span></p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Maximum Numbers Per User</Label>
                    <Input type="number" min={0} value={form.max_unique_numbers ?? ""} onChange={(e) => setForm({ ...form, max_unique_numbers: e.target.value === "" ? null : Number(e.target.value) })} />
                  </div>
                  <div>
                    <Label>Max Numbers With Same Amount</Label>
                    <Input type="number" min={0} value={form.max_same_amount_numbers ?? ""} onChange={(e) => setForm({ ...form, max_same_amount_numbers: e.target.value === "" ? null : Number(e.target.value) })} />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">Leave blank or 0 to disable. Counts reset only after this game's next result is declared.</p>
              </div>


              <div className="col-span-2 flex items-center justify-between rounded-lg bg-muted p-3">
                <Label>Active</Label>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
              <Button onClick={save} className="col-span-2 gradient-primary">Save Game</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <GameDebugPanel games={data ?? []} />

      <div className="overflow-x-auto rounded-2xl bg-card shadow-card">
        <table data-sticky-col className="w-full min-w-[720px] text-sm whitespace-nowrap">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Times</th>
              <th className="px-4 py-3">Bet Range</th><th className="px-4 py-3">Active</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((g: any) => (
              <tr key={g.id} className="border-t">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center rounded-lg text-xs font-black text-white" style={{ background: g.color }}>{g.name[0]}</span>
                    <span className="font-semibold">{g.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {g.open_time?.slice(0, 5)} → {g.close_time?.slice(0, 5)} · R {g.result_time?.slice(0, 5)}
                </td>
                <td className="px-4 py-3 text-xs">
                  {g.rule_a_start && g.rule_a_end && (
                    <div className="text-[10px] text-muted-foreground">Slab A {g.rule_a_start.slice(0,5)}–{g.rule_a_end.slice(0,5)}: ₹{g.rule_a_min}–₹{g.rule_a_max}</div>
                  )}
                  {g.rule_b_start && g.rule_b_end && (
                    <div className="text-[10px] text-muted-foreground">Slab B {g.rule_b_start.slice(0,5)}–{g.rule_b_end.slice(0,5)}: ₹{g.rule_b_min}–₹{g.rule_b_max}</div>
                  )}
                  {!g.rule_a_start && !g.rule_b_start && (
                    <div className="text-[10px] text-muted-foreground">No slab configured — no bet-amount limit.</div>
                  )}
                </td>
                <td className="px-4 py-3"><Switch checked={g.active} onCheckedChange={(v) => toggle(g.id, v)} /></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => { setForm({
                    ...g,
                    open_time: g.open_time?.slice(0,5) ?? "",
                    close_time: g.close_time?.slice(0,5) ?? "",
                    result_time: g.result_time?.slice(0,5) ?? "",
                    rule_a_start: g.rule_a_start?.slice(0,5) ?? "",
                    rule_a_end: g.rule_a_end?.slice(0,5) ?? "",
                    rule_b_start: g.rule_b_start?.slice(0,5) ?? "",
                    rule_b_end: g.rule_b_end?.slice(0,5) ?? "",
                  }); setOpen(true); }} className="mr-2 text-primary"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(g.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GameDebugPanel({ games }: { games: any[] }) {
  const [nowIst, setNowIst] = useState(() => istTime(new Date()));
  const [open, setOpen] = useState(false);
  // tick every 15s
  useEffect(() => { const id = setInterval(() => setNowIst(istTime(new Date())), 15000); return () => clearInterval(id); }, []);

  const dupNames = new Map<string, number>();
  for (const g of games) dupNames.set(g.name, (dupNames.get(g.name) ?? 0) + 1);
  const duplicates = Array.from(dupNames.entries()).filter(([, c]) => c > 1).map(([n]) => n);

  return (
    <div className="rounded-2xl border bg-card shadow-card">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="text-sm font-black">Debug: Betting Status ({nowIst} IST)</span>
        <span className="text-xs text-muted-foreground">{open ? "Hide" : "Show"}</span>
      </button>
      {duplicates.length > 0 && (
        <div className="border-t bg-destructive/10 px-4 py-2 text-xs font-bold text-destructive">
          ⚠ Duplicate game names detected: {duplicates.join(", ")}. Only the active latest record should be used.
        </div>
      )}
      {open && (
        <div className="overflow-x-auto border-t">
          <table className="w-full text-[11px]">
            <thead className="bg-muted/50 text-left uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">game_id</th>
                <th className="px-3 py-2">open</th>
                <th className="px-3 py-2">close / bet_until</th>
                <th className="px-3 py-2">min–max</th>
                <th className="px-3 py-2">active</th>
                <th className="px-3 py-2">betting_allowed</th>
              </tr>
            </thead>
            <tbody>
              {games.map((g) => {
                const closeEff = g.bet_until ?? g.close_time;
                const allowed = bettingAllowed(g.active, g.open_time, closeEff);
                return (
                  <tr key={g.id} className="border-t">
                    <td className="px-3 py-1.5 font-semibold">{g.name}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px]">{g.id}</td>
                    <td className="px-3 py-1.5">{g.open_time?.slice(0,5) ?? "-"}</td>
                    <td className="px-3 py-1.5">{closeEff?.slice(0,5) ?? "-"}</td>
                    <td className="px-3 py-1.5">—</td>
                    <td className="px-3 py-1.5">{String(g.active)}</td>
                    <td className={"px-3 py-1.5 font-bold " + (allowed ? "text-green-600" : "text-destructive")}>{allowed ? "YES" : "NO"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function istTime(d: Date): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(d);
}
function toMin(t?: string | null): number | null {
  if (!t) return null; const [h, m] = t.split(":"); const H = Number(h), M = Number(m);
  if (isNaN(H) || isNaN(M)) return null; return H * 60 + M;
}
function bettingAllowed(active: boolean, open?: string | null, close?: string | null): boolean {
  if (!active) return false;
  const o = toMin(open), c = toMin(close);
  if (o == null || c == null) return true;
  const istNow = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date());
  const [h, m] = istNow.split(":").map(Number); const n = h * 60 + m;
  return c >= o ? n >= o && n < c : n >= o || n < c;
}
